// SPARKLES — shared pools of small motes for the field's feedback: a click's spark running a rice row, a citizen's tending puff,
// a crew's wake of sparks, the harvest's burst of grain. Two InstancedMesh pools (one draw call each): SOFT round glows for puffs,
// the running mote and grain; sharp four-point STARS for the glints and wakes, so a worker's trail reads as scattered sparkle rather
// than fireflies. Fixed-size buffers, no allocation per effect: when a pool is full the effect is skipped. Each quad faces the camera
// (its rotation is the camera's) and fades by darkening (additive blending). Effects out of view, or zoomed far out, are skipped.
// Rates: RiceGrowth.sparksPerSec / tendFxPerSec.
import * as THREE from 'three'
import { RiceGrowth } from '../config/balance.js'

const SOFT_CAP = 128, STAR_CAP = 192
const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _c = new THREE.Color(), _ndc = new THREE.Vector3(), _q = new THREE.Quaternion(), _spin = new THREE.Quaternion(), _z = new THREE.Vector3(0, 0, 1)

function softTexture() {
  const cv = document.createElement('canvas'); cv.width = cv.height = 64
  const ctx = cv.getContext('2d'); const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32)
  g.addColorStop(0, 'rgba(255,255,255,1)'); g.addColorStop(0.3, 'rgba(255,255,255,0.75)'); g.addColorStop(0.7, 'rgba(255,255,255,0.18)'); g.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = g; ctx.fillRect(0, 0, 64, 64)
  const t = new THREE.CanvasTexture(cv); t.colorSpace = THREE.SRGBColorSpace; return t
}
function starTexture() { // a sharp four-point star with a small bright core
  const cv = document.createElement('canvas'); cv.width = cv.height = 64
  const ctx = cv.getContext('2d'); ctx.clearRect(0, 0, 64, 64)
  const ray = (len, w) => { ctx.beginPath(); ctx.moveTo(32 - w, 32); ctx.lineTo(32, 32 - len); ctx.lineTo(32 + w, 32); ctx.lineTo(32, 32 + len); ctx.closePath(); ctx.fill() }
  const turned = (deg, fn) => { ctx.save(); ctx.translate(32, 32); ctx.rotate((deg * Math.PI) / 180); ctx.translate(-32, -32); fn(); ctx.restore() }
  ctx.fillStyle = 'rgba(255,255,255,0.55)'; ray(31, 2.2); turned(90, () => ray(31, 2.2))
  ctx.fillStyle = 'rgba(255,255,255,0.35)'; turned(45, () => ray(16, 1.4)); turned(-45, () => ray(16, 1.4))
  const core = ctx.createRadialGradient(32, 32, 0, 32, 32, 7); core.addColorStop(0, 'rgba(255,255,255,1)'); core.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = core; ctx.fillRect(0, 0, 64, 64)
  const t = new THREE.CanvasTexture(cv); t.colorSpace = THREE.SRGBColorSpace; return t
}

const MOTE = 1, GLINT = 2, PUFF = 3, GRAIN = 4, WAKE = 5

function makePool(scene, texture, cap, renderOrder) {
  const mat = new THREE.MeshBasicMaterial({ map: texture, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false })
  const mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), mat, cap)
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
  mesh.frustumCulled = false
  mesh.renderOrder = renderOrder
  mesh.count = 0
  for (let i = 0; i < cap; i++) mesh.setColorAt(i, _c.setRGB(0, 0, 0))
  mesh.raycast = () => {}
  scene.add(mesh)
  const P = Array.from({ length: cap }, () => ({ kind: 0, x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, z0: 0, z1: 0, age: 0, life: 1, size: 0.06, r: 1, g: 1, b: 1, glints: 0, phase: 0, spin: 0, twinkle: 0 }))
  return { mesh, P, alive: 0, cap }
}

export function createSparkles({ scene, camera }) {
  const soft = makePool(scene, softTexture(), SOFT_CAP, 3)
  const star = makePool(scene, starTexture(), STAR_CAP, 4)
  const stats = { sparks: 0, puffs: 0, wakes: 0, bursts: 0, skipped: 0, lastMs: 0, lastSkip: null, zoom: 0, ndc: null }

  // Is a world point on screen? Returns { ok, centre } — centre = how far from the middle of the view (0 = dead centre).
  const view = (x, y, z) => {
    stats.zoom = camera.zoom
    if (camera.zoom < 0.85) { stats.lastSkip = 'zoom'; return { ok: false, centre: Infinity } } // zoomed far out: the motes would be sub-pixel
    camera.updateMatrixWorld() // (a click can land between frames)
    _ndc.set(x, y, z).project(camera)
    stats.ndc = [+_ndc.x.toFixed(2), +_ndc.y.toFixed(2)]
    const ok = Math.abs(_ndc.x) < 1.08 && Math.abs(_ndc.y) < 1.08
    if (!ok) stats.lastSkip = 'view'
    return { ok, centre: Math.hypot(_ndc.x, _ndc.y) }
  }
  const inView = (x, y, z) => view(x, y, z).ok
  const take = (pool) => { if (pool.alive < pool.cap) return pool.P[pool.alive++]; stats.skipped++; stats.lastSkip = 'pool'; return null }
  const init = (p, kind, x, y, z, life, size, rgb) => { p.kind = kind; p.x = x; p.y = y; p.z = z; p.vx = p.vy = p.vz = 0; p.age = 0; p.life = life; p.size = size; p.r = rgb[0]; p.g = rgb[1]; p.b = rgb[2]; p.glints = 0; p.phase = Math.random() * 6.28; p.spin = (Math.random() - 0.5) * 6; p.twinkle = 0 }

  // rate limiting: a small ring of timestamps per effect
  const recent = { spark: [], tend: [] }
  const allowed = (key, perSec, now) => { const r = recent[key]; while (r.length && now - r[0] > 1000) r.shift(); if (r.length >= perSec) { stats.lastSkip = 'rate'; return false } r.push(now); return true }

  const GOLD = [1.0, 0.86, 0.42], GREEN = [0.55, 1.0, 0.55], GRAIN_C = [1.0, 0.8, 0.35], WAKE_C = [0.95, 1.0, 0.7]
  // a scattered speck: a tiny star thrown a little way from (x, y, z), twinkling as it fades
  const speck = (x, y, z, rgb, life = 0.4, size = 0.035) => {
    const p = take(star); if (!p) return false
    const a = Math.random() * 6.28, r = Math.random() * 0.07
    init(p, WAKE, x + Math.cos(a) * r, y + 0.03 + Math.random() * 0.05, z + Math.sin(a) * r, life * (0.7 + Math.random() * 0.6), size * (0.6 + Math.random() * 0.9), rgb)
    p.vx = Math.cos(a) * (0.05 + Math.random() * 0.12); p.vz = Math.sin(a) * (0.05 + Math.random() * 0.12); p.vy = 0.08 + Math.random() * 0.14
    p.twinkle = 6 + Math.random() * 10
    return true
  }

  function tickPool(pool, dt, now) {
    let i = 0
    while (i < pool.alive) {
      const p = pool.P[i]
      p.age += dt
      if (p.age >= p.life) { pool.alive--; if (i !== pool.alive) { const t = pool.P[i]; pool.P[i] = pool.P[pool.alive]; pool.P[pool.alive] = t } continue } // swap-remove
      const u = p.age / p.life
      let env = 1, size = p.size
      if (p.kind === MOTE) { // runs the row, bobbing, and throws a star speck every 45 ms
        p.z = p.z0 + (p.z1 - p.z0) * u; p.y += Math.sin(u * 18 + p.phase) * 0.0012
        env = u < 0.15 ? u / 0.15 : u > 0.85 ? (1 - u) / 0.15 : 1
        const g = Math.floor(p.age / 0.045)
        if (g > p.glints) { p.glints = g; speck(p.x, p.y - 0.02, p.z, GOLD, 0.35, 0.04) }
      } else if (p.kind === GLINT) { env = 1 - u; size = p.size * (1 + 0.6 * u) }
      else if (p.kind === WAKE) { p.x += p.vx * dt; p.y += p.vy * dt; p.z += p.vz * dt; p.vy -= 0.12 * dt; env = (1 - u) * (0.55 + 0.45 * Math.abs(Math.sin(now * p.twinkle + p.phase))); size = p.size * (1 - 0.4 * u) }
      else if (p.kind === PUFF) { p.y += p.vy * dt; env = 1 - u * u; size = p.size * (1 + u) }
      else if (p.kind === GRAIN) { p.vy -= 2.6 * dt; p.x += p.vx * dt; p.y += p.vy * dt; p.z += p.vz * dt; if (p.y < 0.33) { p.y = 0.33; p.vy = 0; p.vx *= 0.6; p.vz *= 0.6 } env = u < 0.7 ? 1 : (1 - u) / 0.3 }
      _p.set(p.x, p.y, p.z); _s.setScalar(size)
      _q.copy(camera.quaternion); if (p.spin) _q.multiply(_spin.setFromAxisAngle(_z, p.spin * p.age))
      _m.compose(_p, _q, _s)
      pool.mesh.setMatrixAt(i, _m)
      pool.mesh.setColorAt(i, _c.setRGB(p.r * env, p.g * env, p.b * env))
      i++
    }
    pool.mesh.count = pool.alive
    pool.mesh.instanceMatrix.needsUpdate = true
    if (pool.mesh.instanceColor) pool.mesh.instanceColor.needsUpdate = true
  }

  return {
    mesh: soft.mesh, starMesh: star.mesh,
    inView: view,
    // A scatter of n coloured specks at a point (feathers, gold) — for the crow and the glint.
    specks(x, y, z, n = 4, rgb = GOLD, life = 0.5, size = 0.045) { if (!inView(x, y, z)) return false; let m = 0; for (let i = 0; i < n; i++) if (speck(x, y, z, rgb, life, size)) m++; return m > 0 },
    // A world point in CSS pixels (the canvas fills the window) — where a floating number should start.
    toScreen(x, y, z) { camera.updateMatrixWorld(); _ndc.set(x, y, z).project(camera); const r = document.getElementById('scene')?.getBoundingClientRect() ?? { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight }; return { x: r.left + (_ndc.x + 1) / 2 * r.width, y: r.top + (1 - _ndc.y) / 2 * r.height } },
    // A click's spark: a soft mote runs along a row from z0 to z1 at height y, throwing star specks behind it.
    spark(row, now = performance.now()) {
      if (!row || !inView(row.x, row.y, (row.z0 + row.z1) / 2) || !allowed('spark', RiceGrowth.sparksPerSec, now)) { stats.skipped++; return false }
      const p = take(soft); if (!p) return false
      init(p, MOTE, row.x, row.y + 0.07, row.z0, RiceGrowth.sparkMs / 1000, 0.09, GOLD)
      p.z0 = row.z0; p.z1 = row.z1
      stats.sparks++
      return true
    },
    // A citizen's tending puff at its feet (rate-limited across all citizens): a soft green puff plus a few specks.
    puff(x, y, z, rgb = GREEN, now = performance.now()) {
      if (!inView(x, y, z) || !allowed('tend', RiceGrowth.tendFxPerSec, now)) { stats.skipped++; return false }
      for (let i = 0; i < 2; i++) {
        const p = take(soft); if (!p) return false
        init(p, PUFF, x + (Math.random() - 0.5) * 0.08, y + 0.04, z + (Math.random() - 0.5) * 0.08, 0.5 + Math.random() * 0.2, 0.07, rgb); p.vy = 0.12 + Math.random() * 0.08
      }
      for (let i = 0; i < 3; i++) speck(x, y, z, rgb, 0.45, 0.035)
      stats.puffs++
      return true
    },
    // A crew worker's wake: a scatter of tiny twinkling stars at its feet (the caller spaces the emissions).
    wake(x, y, z) {
      if (!inView(x, y, z)) return false
      let n = 0
      for (let i = 0; i < 3; i++) if (speck(x, y, z, WAKE_C, 0.42, 0.034)) n++
      if (n) stats.wakes++
      return n > 0
    },
    // The harvest: a burst of grain from a point.
    burst(x, y, z, n = 18, rgb = GRAIN_C) {
      if (!inView(x, y, z)) { stats.skipped++; return false }
      for (let i = 0; i < n; i++) { const p = take(soft); if (!p) break; init(p, GRAIN, x, y + 0.1, z, 0.9 + Math.random() * 0.4, 0.05, rgb); const a = Math.random() * 6.28, sp = 0.4 + Math.random() * 0.6; p.vx = Math.cos(a) * sp; p.vz = Math.sin(a) * sp; p.vy = 0.9 + Math.random() * 0.7 }
      for (let i = 0; i < 6; i++) speck(x, y + 0.2, z, GOLD, 0.6, 0.05)
      stats.bursts++
      return true
    },
    tick(dt) {
      const t0 = performance.now(), now = t0 * 0.001
      if (soft.alive) tickPool(soft, dt, now); else if (soft.mesh.count) soft.mesh.count = 0
      if (star.alive) tickPool(star, dt, now); else if (star.mesh.count) star.mesh.count = 0
      stats.lastMs = performance.now() - t0
    },
    alive: () => soft.alive + star.alive,
    stats: () => ({ ...stats, alive: soft.alive + star.alive, soft: soft.alive, star: star.alive, cap: SOFT_CAP + STAR_CAP }),
  }
}
